import { useEffect, useState } from "react";
import {
  CheckCircle2,
  Clock,
  Loader2,
  RotateCcw,
  Play,
  Workflow,
  Brain,
  CalendarDays,
  PlayCircle,
  XCircle,
  AlertTriangle,
  UploadCloud,
  SearchCheck,
  Send,
  ShieldCheck,
  UserRound,
} from "lucide-react";
import { formatDeadline } from "@app/utils/auth-policy";
import { Button } from "@app/components/ui/button";
import { Badge } from "@app/components/ui/badge";
import { Plural, Trans, useLingui } from "@lingui/macro";
import dayjs from "dayjs";
import type { MageXPatientWithFeedbacks } from "@api/src/types/patient";

type StepStatus = "done" | "running" | "pending" | "error" | "warning";
export type StepOutcome = "success" | "no-results" | "error" | null;

// Ce que le registre attend encore, dans SES termes : rien n'est déduit côté
// Mage-X, la liste vient du `.Import.Log` écrit par NECSWS. Une exigence que nous
// n'avons jamais rencontrée s'affiche donc sans changement de code.
export interface EurospineRegistryRequirement {
  questionname: string;
  label: string;
  message: string;
}

export interface EurospineRunInfo {
  ok: boolean;
  created_at: string | null;
  filename: string | null;
  error_message: string | null;
  // Dossier NECSWS où le fichier a été classé (Complete / InComplete /
  // NotImported), ou ReadyToProcess tant que leur parseur ne l'a pas pris.
  // null = log pas encore lu.
  registry_status: string | null;
  registry_valid: boolean | null;
  registry_pending: EurospineRegistryRequirement[];
  registry_checked_at: string | null;
  // `true` quand le registre en a fini avec le fichier (Complete / InComplete /
  // NotImported) : relire son log ne peut plus rien changer. La page s'en sert
  // pour cesser d'interroger NECSWS une fois le verdict rendu.
  registry_final: boolean;
}

export interface EurospineMissingField {
  questionname: string;
  label: string;
  // Le champ Mage-X d'où la question est construite, `null` pour les questions
  // qu'aucun clinicien ne peut remplir (chirurgien de la session, constantes
  // « non connu », enveloppe d'identité).
  field_key?: string | null;
}

// Les questions requises manquantes, regroupées par le champ de la fiche
// patient qu'il faut aller remplir. Un seul champ alimente parfois sept
// questions du registre : listées sous le libellé du registre, elles se lisent
// comme sept reproches dont aucun ne nomme une case ouvrable.
export interface EurospineMissingFieldGroup {
  field_key: string;
  // Libellé de l'ontologie — celui de la fiche patient. `null` quand
  // l'ontologie ne déclare pas le champ : on retombe alors sur la liste brute.
  label: string | null;
  questionnames: string[];
}

// Une incohérence entre réponses : le registre refuse un dossier dont chaque
// champ requis est pourtant rempli (ex. date de sortie antérieure à la date
// d'intervention). `blocking` = refus certain du registre, `warning` = donnée
// qui se contredit mais que le registre laisse passer.
export interface EurospineCoherenceViolation {
  code: string;
  severity: "blocking" | "warning";
  questionnames: string[];
  // Phrase française prête à afficher, construite côté back à partir des
  // valeurs réelles. Sert de repli pour tout `code` que le front ne connaît pas.
  message: string;
  label?: string;
  value?: string;
  reference_label?: string;
  reference_value?: string;
  // Chronologie : vrai quand l'égalité des deux dates est elle aussi refusée
  // (on ne naît pas le jour de sa propre intervention).
  strict_ordering?: boolean;
  min?: string | null;
  max?: string | null;
  units?: string | null;
}

export interface EurospineVerifyResult {
  valid: boolean;
  missing_required: EurospineMissingField[];
  missing_fields: EurospineMissingFieldGroup[];
  incoherent: EurospineCoherenceViolation[];
  populated: string[];
  surgeon: string | null;
  xml_bytes: number;
}

/** Whether the operating surgeon's engagement covers this patient (read once the dossier is). */
export interface EurospineConsent {
  covered: boolean;
  reason: string | null;
  explanation: string;
  surgeon: string | null;
  informed_since: string | null;
}

// Grouped into a single optional prop to keep the EuroSpine step fully
// decoupled: when `eurospine` is undefined the step does not render and the
// rest of the pipeline is unchanged.
export interface EurospineStep {
  eligible: boolean;
  lastRun: EurospineRunInfo | null;
  // Le dernier transfert RÉUSSI, qui n'est pas le dernier essai : une nouvelle
  // tentative échouée après un transfert réussi masquerait sinon le fait que le
  // patient est bel et bien dans le registre. C'est cette ligne-là qui répond à
  // « ai-je déjà envoyé ce patient ? », la seule question qu'un chirurgien ne
  // doit pas avoir à se poser deux fois.
  lastSuccess: EurospineRunInfo | null;
  verifyResult: EurospineVerifyResult | null;
  verifying: boolean;
  sending: boolean;
  outcome: StepOutcome;
  onVerify: () => void;
  onSend: () => void;
  // Relit le verdict du registre pour le dernier transfert : NECSWS parse le
  // fichier quelques minutes après le dépôt, donc l'état arrive après coup.
  refreshingRegistry: boolean;
  onRefreshRegistry: () => void;
  // The registry XML states the patient's consent: only a patient their surgeon's engagement
  // covers is sent, and a patient who refuses is never sent.
  consent: EurospineConsent | null;
  refused: boolean;
  togglingRefusal: boolean;
  onToggleRefusal: () => void;
}

interface PipelineViewProps {
  patient: MageXPatientWithFeedbacks;
  onSync?: () => Promise<void> | void;
  syncRunning?: boolean;
  canRunPipeline?: boolean;
  syncOutcome?: StepOutcome;
  /** The doc-first reading of this dossier is in flight. */
  reading?: boolean;
  readOutcome?: StepOutcome;
  /** When the doc-first chain read this dossier (`PatientDossierReading.read_at`), if it did. */
  dossierReadAt?: string | null;
  /** Re-read this dossier through the doc-first chain. */
  onReadDossier?: () => void | Promise<void>;
  eurospine?: EurospineStep;
}

/**
 * Explains one coherence violation, in the viewer's language.
 *
 * The backend already ships a complete French sentence in `message`; rebuilding
 * it here from the structured parts is what makes the explanation translatable.
 * Any `code` this component does not know falls back to `message`, so a new
 * backend rule shows up in the UI immediately — in French only, until a case is
 * added here.
 */
function CoherenceExplanation({
  violation,
}: {
  violation: EurospineCoherenceViolation;
}) {
  const { label, value, reference_label, reference_value, min, max, units } =
    violation;
  const unitSuffix = units ? ` ${units}` : "";

  switch (violation.code) {
    case "chronology":
      if (!label || !value || !reference_label || !reference_value) break;
      return violation.strict_ordering ? (
        <Trans>
          « {label} » ({value}) est antérieure ou égale à « {reference_label} »
          ({reference_value}). Le registre refuse le dossier tant que les deux
          dates ne sont pas remises dans l'ordre.
        </Trans>
      ) : (
        <Trans>
          « {label} » ({value}) est antérieure à « {reference_label} » (
          {reference_value}). Le registre refuse le dossier tant que les deux
          dates ne sont pas remises dans l'ordre.
        </Trans>
      );

    case "out_of_range":
      if (!label || !value) break;
      return (
        <Trans>
          « {label} » ({value}
          {unitSuffix}) sort des bornes acceptées par le registre ({min} – {max}
          {unitSuffix}).
        </Trans>
      );

    case "future_date":
      if (!label || !value) break;
      return (
        <Trans>
          « {label} » ({value}) est dans le futur — l'épisode de soins décrit n'a
          pas encore eu lieu.
        </Trans>
      );

    case "derived_mismatch":
      if (!label || !value || !reference_label || !reference_value) break;
      return (
        <Trans>
          « {label} » ({value}) ne correspond pas à {reference_label} (
          {reference_value}) transmis avec le dossier.
        </Trans>
      );
  }

  return <>{violation.message}</>;
}

function TimelineStepIcon({ status }: { status: StepStatus }) {
  const base =
    "h-9 w-9 rounded-full flex items-center justify-center shrink-0";
  if (status === "done")
    return (
      <div className={`${base} bg-green-100 border-2 border-green-400`}>
        <CheckCircle2 className="h-4 w-4 text-green-600" />
      </div>
    );
  if (status === "running")
    return (
      <div className={`${base} bg-blue-50 border-2 border-primary`}>
        <Loader2 className="h-4 w-4 text-primary animate-spin" />
      </div>
    );
  if (status === "error")
    return (
      <div className={`${base} bg-red-50 border-2 border-red-300`}>
        <XCircle className="h-4 w-4 text-red-500" />
      </div>
    );
  if (status === "warning")
    return (
      <div className={`${base} bg-amber-50 border-2 border-amber-300`}>
        <AlertTriangle className="h-4 w-4 text-amber-500" />
      </div>
    );
  return (
    <div className={`${base} bg-slate-50 border-2 border-slate-200`}>
      <Clock className="h-4 w-4 text-slate-300" />
    </div>
  );
}

function StepBadge({
  status,
  warningLabel,
}: {
  status: StepStatus;
  // « Aucun résultat » est le mot juste pour la lecture, où l'avertissement
  // signifie que le modèle n'a rien trouvé. Sur l'étape EuroSpine il ne veut rien
  // dire : l'avertissement y signifie que le registre a refusé le dossier. Le
  // libellé appartient donc à l'étape, pas au badge.
  warningLabel?: React.ReactNode;
}) {
  if (status === "done")
    return (
      <Badge className="bg-green-100 text-green-700 border-green-200 text-3xs px-1.5 py-0 font-medium hover:bg-green-100">
        <Trans>Complété</Trans>
      </Badge>
    );
  if (status === "running")
    return (
      <Badge className="bg-blue-50 text-primary border-primary/30 text-3xs px-1.5 py-0 font-medium hover:bg-blue-50">
        <Trans>En cours</Trans>
      </Badge>
    );
  if (status === "error")
    return (
      <Badge className="bg-red-50 text-red-600 border-red-200 text-3xs px-1.5 py-0 font-medium hover:bg-red-50">
        <Trans>Échec</Trans>
      </Badge>
    );
  if (status === "warning")
    return (
      <Badge className="bg-amber-50 text-amber-600 border-amber-200 text-3xs px-1.5 py-0 font-medium hover:bg-amber-50">
        {warningLabel ?? <Trans>Aucun résultat</Trans>}
      </Badge>
    );
  return (
    <Badge className="bg-slate-50 text-slate-400 border-slate-200 text-3xs px-1.5 py-0 font-medium hover:bg-slate-50">
      <Trans>En attente</Trans>
    </Badge>
  );
}

export default function PipelineView({
  patient,
  onSync,
  syncRunning,
  canRunPipeline,
  syncOutcome,
  reading = false,
  readOutcome,
  dossierReadAt,
  onReadDossier,
  eurospine,
}: PipelineViewProps) {
  const { t } = useLingui();

  const readByDossierChain = Boolean(dossierReadAt);

  // Elapsed-time counter shown next to the spinner: a long call must not look like a frozen page.
  const [readElapsedSec, setReadElapsedSec] = useState(0);
  useEffect(() => {
    if (!reading) {
      setReadElapsedSec(0);
      return;
    }
    const start = Date.now();
    const interval = setInterval(() => {
      setReadElapsedSec(Math.floor((Date.now() - start) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, [reading]);

  // Integer seconds, so the counter does not flicker.
  const formatLiveElapsed = (s: number) => {
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    const rs = s % 60;
    return `${m}min ${rs.toString().padStart(2, "0")}s`;
  };

  const step1Status: StepStatus = syncRunning
    ? "running"
    : syncOutcome === "error"
      ? "error"
      : patient.imported_at
        ? "done"
        : "pending";
  const step2Status: StepStatus = reading
    ? "running"
    : readByDossierChain
      ? "done"
      : readOutcome === "error"
        ? "error"
        : readOutcome === "no-results"
          ? "warning"
          : "pending";

  const showEurospine = !!eurospine?.eligible;
  // Déposer le fichier chez NECSWS et être accepté par EuroSpine sont deux
  // choses : `ok` ne dit que la première. Tant que le registre n'a pas répondu,
  // vert = « transmis » ; dès qu'il a répondu non, orange — la liste « À
  // compléter dans EuroSpine » s'affiche déjà en dessous, et un bandeau vert
  // au-dessus d'elle la contredisait.
  const eurospineRejected = eurospine?.lastSuccess?.registry_valid === false;
  const eurospineStatus: StepStatus = eurospine?.sending
    ? "running"
    : eurospine?.outcome === "error"
      ? "error"
      : eurospine?.lastSuccess
        ? eurospineRejected
          ? "warning"
          : "done"
        : eurospine?.lastRun && !eurospine.lastRun.ok
          ? "error"
          : eurospine?.verifyResult && !eurospine.verifyResult.valid
            ? "warning"
            : "pending";
  const eurospineMissing = eurospine?.verifyResult?.missing_required ?? [];
  // Ce que le médecin doit aller remplir, nommé comme sur sa fiche patient.
  // On ne garde que les groupes que l'ontologie sait nommer : un `field_key`
  // brut ne lui dirait rien de plus que le libellé du registre.
  const eurospineMissingFields = (
    eurospine?.verifyResult?.missing_fields ?? []
  ).filter((g) => !!g.label);
  // Les questions qu'aucun champ de la fiche n'alimente (chirurgien de la
  // session, constantes « non connu », identité) restent listées telles quelles
  // sous le libellé du registre : c'est le seul mot juste pour elles.
  const eurospineMissingUngrouped = eurospineMissing.filter(
    (f) =>
      !f.field_key ||
      !eurospineMissingFields.some((g) => g.field_key === f.field_key),
  );
  const eurospineIncoherent = eurospine?.verifyResult?.incoherent ?? [];
  // Seules les incohérences `blocking` retiennent le transfert : ce sont celles
  // dont on sait que le registre les refuse. Les `warning` sont affichées telles
  // quelles, sans jamais bloquer un envoi que le registre accepterait.
  const eurospineBlocking = eurospineIncoherent.filter(
    (v) => v.severity === "blocking",
  );
  const eurospineWarnings = eurospineIncoherent.filter(
    (v) => v.severity !== "blocking",
  );
  const eurospineRegistryPending =
    eurospine?.lastSuccess?.registry_pending ?? [];
  // Renvoyer un patient que le registre détient déjà est une CORRECTION, pas un
  // doublon : le MRN vient de la clé métier, donc EuroSpine reconnaît le dossier
  // et le met à jour (mesuré le 09/09/2026 — patient et formulaire « Updated »,
  // 48 réponses réécrites). Le bouton le dit avec ses mots.
  const alreadyTransferred = !!eurospine?.lastSuccess;
  const canSendEurospine =
    !!eurospine?.verifyResult?.valid && !!eurospine?.consent?.covered;
  const coveringSurgeon = eurospine?.consent?.surgeon ?? "";
  const informedSince =
    formatDeadline(eurospine?.consent?.informed_since ?? null) ?? "";

  const isAnyRunning = syncRunning || reading;

  const handleRunAll = async () => {
    if (onSync) await onSync();
    if (onReadDossier) await onReadDossier();
  };

  return (
    <div className="p-6 max-w-2xl space-y-8">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
          <Workflow className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h2 className="text-sm font-semibold text-slate-900">
            <Trans>Workflow Pipeline</Trans>
          </h2>
          <p className="text-xs text-slate-500">
            <Trans>Suivi des étapes de traitement du dossier</Trans>
          </p>
        </div>
      </div>

      {/* Timeline */}
      <div>
        <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-5">
          <Trans>Étapes</Trans>
        </h3>

        <div className="relative">
          {/* Vertical connector */}
          <div className="absolute left-[17px] top-9 bottom-9 w-px bg-slate-200" />

          <div className="space-y-1">
            {/* Step 1 — Synchronisation */}
            <div className="flex gap-4 pb-6">
              <TimelineStepIcon status={step1Status} />
              <div className="flex-1 min-w-0 pt-1.5">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-semibold text-slate-800">
                    <Trans>Synchronisation</Trans>
                  </span>
                  <StepBadge status={step1Status} />
                  {canRunPipeline && onSync && (
                    <button
                      onClick={onSync}
                      disabled={syncRunning || reading}
                      title={t`Lancer la synchronisation`}
                      className="ml-auto flex items-center gap-1 text-3xs text-primary hover:text-primary/80 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      {syncRunning ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <PlayCircle className="h-3.5 w-3.5" />
                      )}
                      <Trans>Lancer</Trans>
                    </button>
                  )}
                </div>
                <p className="text-xs text-slate-500 mt-0.5">
                  <Trans>Import depuis la source</Trans>
                </p>
                {patient.imported_at && (
                  <div className="flex items-center gap-1 mt-1.5">
                    <CalendarDays className="h-3 w-3 text-slate-400" />
                    <span className="text-xs text-slate-500">
                      {dayjs(patient.imported_at).format("DD/MM/YYYY à HH:mm")}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Step 2 — Lecture du dossier (doc-first chain) */}
            <div className={`flex gap-4 ${showEurospine ? "pb-6" : ""}`}>
              <TimelineStepIcon status={step2Status} />
              <div className="flex-1 min-w-0 pt-1.5">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-semibold text-slate-800">
                    <Trans>Lecture du dossier</Trans>
                  </span>
                  <StepBadge status={step2Status} />
                  {readByDossierChain && (
                    <span className="text-xs text-slate-500">
                      {dayjs(dossierReadAt).format("DD/MM/YYYY")}
                    </span>
                  )}
                  <Brain className="h-3 w-3 text-primary/70" />
                  {canRunPipeline && onReadDossier && (
                    <button
                      onClick={() => void onReadDossier()}
                      disabled={reading}
                      title={readByDossierChain ? t`Relire le dossier` : t`Lire le dossier`}
                      className="ml-auto flex items-center gap-1 text-3xs text-primary hover:text-primary/80 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      {reading ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : readByDossierChain ? (
                        <RotateCcw className="h-3 w-3" />
                      ) : (
                        <PlayCircle className="h-3.5 w-3.5" />
                      )}
                      {reading ? (
                        <span className="tabular-nums font-medium">
                          {formatLiveElapsed(readElapsedSec)}
                        </span>
                      ) : readByDossierChain ? (
                        <Trans>Re-lancer</Trans>
                      ) : (
                        <Trans>Lancer</Trans>
                      )}
                    </button>
                  )}
                </div>
                <p className="text-xs text-slate-500 mt-0.5">
                  {reading ? (
                    <Trans>Lecture du dossier en cours…</Trans>
                  ) : (
                    <Trans>Lecture des documents du dossier par modèle de langage</Trans>
                  )}
                </p>
              </div>
            </div>

            {/* Step 3 — Transfert EuroSpine (only when the patient is opted in
                via the EuroSpine dataset). Decoupled from sync / reading. */}
            {showEurospine && eurospine && (
              <div className="flex gap-4">
                <TimelineStepIcon status={eurospineStatus} />
                <div className="flex-1 min-w-0 pt-1.5">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-slate-800">
                      <Trans>Transfert EuroSpine</Trans>
                    </span>
                    <StepBadge
                      status={eurospineStatus}
                      warningLabel={
                        eurospineRejected ? (
                          <Trans>Refusé par le registre</Trans>
                        ) : (
                          <Trans>Données incomplètes</Trans>
                        )
                      }
                    />
                    <UploadCloud className="h-3 w-3 text-primary/70" />
                    {canRunPipeline && (
                      <div className="ml-auto flex items-center gap-3">
                        <button
                          onClick={eurospine.onVerify}
                          disabled={eurospine.verifying || eurospine.sending}
                          title={t`Vérifier les données avant le transfert`}
                          className="flex items-center gap-1 text-3xs text-primary hover:text-primary/80 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        >
                          {eurospine.verifying ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <SearchCheck className="h-3.5 w-3.5" />
                          )}
                          <Trans>Vérifier</Trans>
                        </button>
                        <button
                          onClick={eurospine.onSend}
                          disabled={
                            !canSendEurospine ||
                            eurospine.sending ||
                            eurospine.verifying
                          }
                          title={
                            eurospine.consent && !eurospine.consent.covered
                              ? eurospine.consent.explanation
                              : !canSendEurospine
                                ? t`Vérifiez d'abord les données du patient`
                                : alreadyTransferred
                                  ? t`Mettre à jour ce dossier dans le registre EuroSpine`
                                  : t`Transférer vers le registre EuroSpine`
                          }
                          className="flex items-center gap-1 text-3xs text-primary hover:text-primary/80 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        >
                          {eurospine.sending ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <Send className="h-3.5 w-3.5" />
                          )}
                          {alreadyTransferred ? (
                            <Trans>Corriger</Trans>
                          ) : (
                            <Trans>Transférer</Trans>
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">
                    <Trans>
                      Envoi du dossier vers le registre EuroSpine (SpineTango)
                    </Trans>
                  </p>

                  {/* Who covers this patient — the operating surgeon's engagement, given once in
                      their configuration — or why nobody does. The send button follows it. */}
                  {eurospine.consent && (
                    <div className="mt-2 flex items-start gap-1.5">
                      <ShieldCheck className="h-3.5 w-3.5 text-slate-400 shrink-0 mt-0.5" />
                      <span className="text-xs text-slate-600">
                        {eurospine.consent.covered ? (
                          <Trans>
                            Couvert par l'engagement de {coveringSurgeon}{" "}
                            (patients informés depuis le {informedSince}).
                          </Trans>
                        ) : (
                          eurospine.consent.explanation
                        )}
                      </span>
                    </div>
                  )}
                  {canRunPipeline && (
                    <button
                      onClick={eurospine.onToggleRefusal}
                      disabled={eurospine.togglingRefusal}
                      className="mt-1 text-3xs text-slate-500 hover:text-slate-700 hover:underline disabled:opacity-40"
                    >
                      {eurospine.refused ? (
                        <Trans>Annuler le refus du patient</Trans>
                      ) : (
                        <Trans>Le patient refuse le registre</Trans>
                      )}
                    </button>
                  )}

                  {/* Operating surgeon — the practitioner linked to THIS patient, not the
                      logged-in user: the transfer is driven by an admin or a MAGE-X reviewer, who
                      is usually not the operator. */}
                  {eurospine.verifyResult && (
                    <div className="mt-2 flex items-center gap-1.5">
                      <UserRound className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                      {eurospine.verifyResult.surgeon ? (
                        <span className="text-xs text-slate-600">
                          <Trans>Chirurgien</Trans> :{" "}
                          {eurospine.verifyResult.surgeon}
                        </span>
                      ) : (
                        <span className="text-xs text-amber-700">
                          <Trans>
                            Chirurgien introuvable — aucun praticien avec un nom
                            complet n'est rattaché à ce patient.
                          </Trans>
                        </span>
                      )}
                    </div>
                  )}

                  {/* Verification result */}
                  {eurospine.verifyResult && eurospineMissing.length > 0 && (
                    <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 p-2.5">
                      <div className="flex items-center gap-1.5 mb-1.5">
                        <AlertTriangle className="h-3.5 w-3.5 text-amber-500 shrink-0" />
                        <span className="text-xs font-medium text-amber-700">
                          <Trans>
                            Champs manquants pour le transfert (indicatif)
                          </Trans>
                        </span>
                      </div>
                      {/* Nommés comme sur la fiche patient, et regroupés : un
                          seul champ vide alimente parfois sept questions du
                          registre, et aucun de ces sept intitulés ne désigne
                          une case que le médecin peut ouvrir. */}
                      <ul className="space-y-0.5 pl-5 list-disc">
                        {eurospineMissingFields.map((g) => (
                          <li
                            key={g.field_key}
                            className="text-2xs text-amber-700"
                          >
                            {g.label}
                            {g.questionnames.length > 1 && (
                              <span className="text-amber-600">
                                {" — "}
                                <Plural
                                  value={g.questionnames.length}
                                  one="# réponse EuroSpine en dépend"
                                  other="# réponses EuroSpine en dépendent"
                                />
                              </span>
                            )}
                          </li>
                        ))}
                        {eurospineMissingUngrouped.map((f) => (
                          <li
                            key={f.questionname}
                            className="text-2xs text-amber-700"
                          >
                            {f.label}
                          </li>
                        ))}
                      </ul>
                      <p className="text-3xs text-amber-600 mt-1.5">
                        <Trans>
                          Le registre risque de refuser le transfert tant que
                          ces champs ne sont pas renseignés.
                        </Trans>
                      </p>
                    </div>
                  )}
                  {/* Incohérences bloquantes — chaque champ est rempli, mais deux
                      réponses se contredisent et le registre refusera le dossier.
                      C'est le cas qu'aucun contrôle de présence ne peut voir. */}
                  {eurospine.verifyResult && eurospineBlocking.length > 0 && (
                    <div className="mt-2 rounded-lg border border-red-200 bg-red-50 p-2.5">
                      <div className="flex items-center gap-1.5 mb-1.5">
                        <XCircle className="h-3.5 w-3.5 text-red-500 shrink-0" />
                        <span className="text-xs font-medium text-red-600">
                          <Trans>Données incohérentes entre elles</Trans>
                        </span>
                      </div>
                      <ul className="space-y-1 pl-5 list-disc">
                        {eurospineBlocking.map((v) => (
                          <li
                            key={`${v.code}-${v.questionnames.join("-")}`}
                            className="text-2xs text-red-600"
                          >
                            <CoherenceExplanation violation={v} />
                          </li>
                        ))}
                      </ul>
                      <p className="text-3xs text-red-500 mt-1.5">
                        <Trans>
                          Corrigez ces valeurs sur la fiche patient avant de
                          relancer la vérification : le registre classerait le
                          dossier en « InComplete ».
                        </Trans>
                      </p>
                    </div>
                  )}

                  {/* Incohérences non bloquantes — signalées, jamais opposées à
                      l'envoi : le registre les accepte. */}
                  {eurospine.verifyResult && eurospineWarnings.length > 0 && (
                    <div className="mt-2 rounded-lg border border-slate-200 bg-slate-50 p-2.5">
                      <div className="flex items-center gap-1.5 mb-1.5">
                        <AlertTriangle className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                        <span className="text-xs font-medium text-slate-700">
                          <Trans>Données à vérifier (non bloquant)</Trans>
                        </span>
                      </div>
                      <ul className="space-y-1 pl-5 list-disc">
                        {eurospineWarnings.map((v) => (
                          <li
                            key={`${v.code}-${v.questionnames.join("-")}`}
                            className="text-2xs text-slate-600"
                          >
                            <CoherenceExplanation violation={v} />
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {eurospine.verifyResult &&
                    eurospineMissing.length === 0 &&
                    eurospineIncoherent.length === 0 && (
                      <div className="mt-2 flex items-center gap-1.5">
                        <CheckCircle2 className="h-3.5 w-3.5 text-green-500 shrink-0" />
                        <span className="text-xs text-green-700">
                          <Trans>Données prêtes pour le transfert</Trans>
                        </span>
                      </div>
                    )}

                  {/* Ce que le dossier a déjà fait chez EuroSpine. En premier et
                      sans condition : un chirurgien qui rouvre une fiche ne doit
                      jamais avoir à se demander s'il l'a déjà envoyée. */}
                  {(eurospine.lastSuccess || eurospine.lastRun) && (
                    <div className="mt-2 space-y-1">
                      {eurospine.lastSuccess?.created_at && (
                        <div className="flex items-center gap-1.5">
                          <CalendarDays className="h-3 w-3 text-slate-400 shrink-0" />
                          <span className="text-xs font-medium text-slate-700">
                            <Trans>Transmis à EuroSpine le</Trans>{" "}
                            {dayjs(eurospine.lastSuccess.created_at).format(
                              "DD/MM/YYYY à HH:mm",
                            )}
                          </span>
                        </div>
                      )}
                      {/* Une tentative en échec. Affichée même APRÈS un transfert
                          réussi : c'est un dépôt qui n'est pas arrivé, pas le
                          dossier qui aurait quitté le registre. */}
                      {eurospine.lastRun &&
                        !eurospine.lastRun.ok &&
                        eurospine.lastRun.error_message && (
                          <div className="flex items-start gap-1.5">
                            <XCircle className="h-3 w-3 text-red-400 shrink-0 mt-0.5" />
                            <span className="text-xs text-red-500">
                              {eurospine.lastRun.created_at && (
                                <>
                                  <Trans>Dernière tentative</Trans>{" "}
                                  {dayjs(eurospine.lastRun.created_at).format(
                                    "DD/MM/YYYY à HH:mm",
                                  )}{" "}
                                  —{" "}
                                </>
                              )}
                              {eurospine.lastRun.error_message}
                            </span>
                          </div>
                        )}

                      {/* Verdict du registre. Déposer le fichier ne veut pas dire
                          qu'EuroSpine l'accepte : il le parse quelques minutes plus
                          tard et écrit ce qui lui manque. On affiche SA réponse. */}
                      {eurospine.lastSuccess && (
                        <div className="space-y-1">
                          {eurospine.lastSuccess.registry_valid === true && (
                            <div className="flex items-center gap-1.5">
                              <CheckCircle2 className="h-3 w-3 text-green-500 shrink-0" />
                              <span className="text-xs text-green-700">
                                <Trans>Dossier accepté par EuroSpine</Trans>
                              </span>
                            </div>
                          )}
                          {/* Refusé sans rien à compléter : le registre a buté avant
                              de valider le formulaire (ré-importation d'un MRN connu,
                              par exemple). Sans cette ligne, l'écran ne dirait rien. */}
                          {eurospineRejected &&
                            eurospineRegistryPending.length === 0 && (
                              <div className="flex items-center gap-1.5">
                                <XCircle className="h-3 w-3 text-amber-500 shrink-0" />
                                <span className="text-xs text-amber-700">
                                  <Trans>
                                    Dossier refusé par EuroSpine — voir le
                                    statut du registre ci-dessous
                                  </Trans>
                                </span>
                              </div>
                            )}

                          {eurospineRegistryPending.length > 0 && (
                            <div className="rounded-lg border border-amber-200 bg-amber-50 p-2.5">
                              <div className="flex items-center gap-1.5 mb-1.5">
                                <AlertTriangle className="h-3.5 w-3.5 text-amber-500 shrink-0" />
                                <span className="text-xs font-medium text-amber-700">
                                  <Trans>
                                    À compléter dans EuroSpine pour que le
                                    dossier soit accepté
                                  </Trans>
                                </span>
                              </div>
                              <ul className="space-y-0.5 pl-5 list-disc">
                                {eurospineRegistryPending.map((item) => (
                                  <li
                                    key={`${item.questionname}-${item.message}`}
                                    className="text-2xs text-amber-700"
                                  >
                                    {item.label} — {item.message}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}

                          <div className="flex items-center gap-1.5">
                            <span className="text-2xs text-slate-500">
                              {eurospine.lastSuccess.registry_checked_at ? (
                                <>
                                  <Trans>Statut registre</Trans> :{" "}
                                  {eurospine.lastSuccess.registry_status ?? "—"}
                                </>
                              ) : (
                                <Trans>Statut registre non encore relevé</Trans>
                              )}
                            </span>
                            <button
                              type="button"
                              onClick={eurospine.onRefreshRegistry}
                              disabled={eurospine.refreshingRegistry}
                              className="text-2xs text-primary hover:underline disabled:opacity-50 inline-flex items-center gap-1"
                            >
                              {eurospine.refreshingRegistry && (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              )}
                              <Trans>Actualiser</Trans>
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Global Run All button — admins and MAGE-X reviewers */}
      {canRunPipeline && (onSync || onReadDossier) && (
        <div className="pt-2 border-t border-slate-100">
          <Button
            size="sm"
            onClick={handleRunAll}
            disabled={isAnyRunning}
            className="w-full h-9 text-xs bg-gradient-to-r from-primary to-primary/90 text-primary-foreground hover:from-primary/90 hover:to-primary/80"
          >
            {isAnyRunning ? (
              <>
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                <Trans>Pipeline en cours...</Trans>
              </>
            ) : (
              <>
                <Play className="h-3.5 w-3.5 mr-1.5" />
                <Trans>Lancer toutes les étapes</Trans>
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
}
